import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { KeyValueType } from '@models/common';
import { ListResponse } from '@models/helpers';
import { FieldTypeEnum } from '@models/module/fields/enums';
import { FilterLogic, FilterModel, FilterOperator } from '@models/module/grid';
import { DataListFields } from '@models/module/main';
import {
  DataListCommandFilterType,
  FiltersToApply,
  LocalFilterModel,
} from '@shared/components/data-list/common/models/data-list-command.interface';
import { HttpService } from '@shared/services';
import { UserStore } from '@signal-store/user.store';

@Injectable({ providedIn: 'root' })
export class LocalFilterService {
  private userStore = inject(UserStore);
  private http = inject(HttpService);
  
  applyOperator(value: any, operator: FilterOperator, filterValue: any): boolean {
    switch (operator) {
      case FilterOperator.Equal:
        if (Array.isArray(filterValue)) {
          return filterValue.some(val => val === value);
        }
        return value === filterValue;
      case FilterOperator.NotEqual:
        if (Array.isArray(filterValue)) {
          return filterValue.every(val => val !== value);
        }
        return value !== filterValue;
      case FilterOperator.Contains:
        return typeof value === 'string' && value.toLowerCase().includes(filterValue.toLowerCase());
      case FilterOperator.DoesNotContain:
        return typeof value === 'string' && !value.toLowerCase().includes(filterValue.toLowerCase());
      case FilterOperator.StartsWith:
        return typeof value === 'string' && value.toLowerCase().startsWith(filterValue.toLowerCase());
      case FilterOperator.EndsWith:
        return typeof value === 'string' && value.toLowerCase().endsWith(filterValue.toLowerCase());
      case FilterOperator.LessThan:
        return value < filterValue;
      case FilterOperator.LessThanOrEqual:
        return value <= filterValue;
      case FilterOperator.MoreThan:
        return value > filterValue;
      case FilterOperator.MoreThanOrEqual:
        return value >= filterValue;
      case FilterOperator.Between:
        const valueDate = new Date(value);
        const valueStart = new Date(filterValue[0]);
        const valueEnd = new Date(filterValue[1]);
        return valueDate >= valueStart && valueDate <= valueEnd;
      case FilterOperator.On:
        return new Date(value).toDateString() === new Date(filterValue).toDateString();
      case FilterOperator.Before:
        return new Date(value) < new Date(filterValue);
      case FilterOperator.OnOrBefore:
        return new Date(value) <= new Date(filterValue);
      case FilterOperator.After:
        return new Date(value) > new Date(filterValue);
      case FilterOperator.OnOrAfter:
        return new Date(value) >= new Date(filterValue);
      //   case FilterOperator.NotOn:
      //     return new Date(value).toDateString() !== new Date(filterValue).toDateString();
      default:
        return false;
    }
  }

  getFilteredList<T>(list: T[], { filters: { filterList: filters, logic = FilterLogic.AND } }: FiltersToApply): T[] {
    if (!filters || filters.length === 0) return list;

    return list.filter(item => {
      const results = filters.map(filter => {
        const field = filter.field as keyof T;
        const fieldValue = item[field];
        return this.applyOperator(fieldValue, filter.operator!, filter.value);
      });

      return logic === FilterLogic.AND ? results.every(Boolean) : results.some(Boolean);
    });
  }

  /**
   * Helper method to construct filters based on fields
   * @param fields - Fields to map to filters
   * @returns - Filters constructed from fields
   */
  mapFieldsToFilters(fields: DataListFields[]): DataListCommandFilterType[] {
    return fields.map(field => ({
      fieldId: field.id,
      name: field.label,
      type: this.getFilterType(field.typeId),
      values: field.typeId === FieldTypeEnum.YesNo && [
        { key: true, value: 'general.yes' },
        { key: false, value: 'general.no' },
      ],
      multipleSelection: !!field.dataSourceId,
      getValues: field.dataSourceId && this.getValues(field.dataSourceId),
    }));
  }
  private getFilterType(typeId: FieldTypeEnum) {
    switch (typeId) {
      case FieldTypeEnum.Date:
        return 'date';
      case FieldTypeEnum.Number:
        return 'number';
      case FieldTypeEnum.YesNo:
        return 'bool';
      default:
        return 'text';
    }
  }

  private getValues(sourceId: number): Observable<KeyValueType[]> {
    const params = {
      id: sourceId,
      contractId: this.userStore.contract()?.id,
    };
    const sendKeyExceptions = [-1009];
    return this.http
      .post<ListResponse<KeyValueType>>('_api/FieldDefinition/SearchDataSourceList', params, { useLoader: false })
      .pipe(
        map(({ data }) => {
          if (!sendKeyExceptions.includes(sourceId)) {
            // In this case the BE waits for the value from select, instead of the key. It should be changed in the BE
            return data.map(({ value }) => ({ key: value, value }));
          }
          return data;
        }),
      );
  }

  //   Mapping the local filters before making the request, to match the format of the filters in the backend
  //   TODO: Refactor filters so we would have the same format in the frontend and backend
  mapLocalFilters(filter: LocalFilterModel): FilterModel {
    if (!filter) return null;
    const { filterList, logic } = filter;
    return {
      logic,
      filters: filterList.map(({ field, operator, fieldTypeId, value }) =>
        this.addOneMoreLayer({ field, operator, fieldTypeId, value }),
      ),
    };
  }

  private addOneMoreLayer(filter: FilterModel, logic = FilterLogic.OR): FilterModel {
    let filters: FilterModel[] = [];
    if (filter.operator === FilterOperator.Between || !Array.isArray(filter.value)) {
      filters = [filter];
    } else {
      filters = filter.value.map(value =>
        this.addOneMoreLayer({ ...filter, value, logic: FilterLogic.OR }, FilterLogic.OR),
      );
    }
    return {
      logic,
      filters,
    };
  }
}
